/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the whole app.
 */

import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle } from 'lucide-react';
import i18next from 'i18next';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({
            error,
            errorInfo,
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
        // Reload the page
        window.location.reload();
    };

    render() {
        const t = i18next.t.bind(i18next);
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
                    <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <AlertTriangle className="w-8 h-8 text-red-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {t('errorBoundary.title')}
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {t('errorBoundary.subtitle')}
                                </p>
                            </div>
                        </div>

                        {this.state.error && (
                            <div className="mb-6">
                                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    {t('errorBoundary.errorLabel')}:
                                </h2>
                                <pre className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 overflow-x-auto text-sm text-red-900 dark:text-red-100">
                                    {this.state.error.toString()}
                                </pre>
                            </div>
                        )}

                        {this.state.errorInfo && (
                            <details className="mb-6">
                                <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    {t('errorBoundary.stackTrace')}
                                </summary>
                                <pre className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs text-gray-700 dark:text-gray-300">
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={this.handleReset}
                                className="btn-primary flex items-center gap-2"
                            >
                                {t('errorBoundary.reloadPage')}
                            </button>
                            <button
                                onClick={() => window.history.back()}
                                className="btn-secondary"
                            >
                                {t('errorBoundary.goBack')}
                            </button>
                        </div>

                        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-sm text-blue-900 dark:text-blue-100">
                                <strong>{t('errorBoundary.tip')}:</strong> {t('errorBoundary.tipText')}
                            </p>
                            <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200 mt-2 space-y-1">
                                <li>{t('errorBoundary.tipClearCache')}</li>
                                <li>{t('errorBoundary.tipCheckConsole')}</li>
                                <li>{t('errorBoundary.tipContactSupport')}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
